import {DateTimeIsoString} from '@aviary/store/common/common.types';

export const NOTIFICATIONS_FEATURE_KEY = 'notifications';

export interface Notification {
  readonly id: number,
  readonly patronId: number,
  readonly patronNotificationType: NotificationType,
  readonly subject: string,
  readonly message: string,
  readonly emailSent: boolean,
  readonly smsSent: boolean,
  readonly active: boolean,
  readonly viewed: boolean,
  readonly created: DateTimeIsoString,
  readonly modified: DateTimeIsoString,

}

export type NotificationType =
  'SELF_REGISTRATION_RESIDENT_APPROVED' |
  'SELF_REGISTRATION_NONRESIDENT_APPROVED' |
  'SELF_REGISTRATION_OUT_OF_BOUNDS_NO_FEE' |
  'SELF_REGISTRATION_INVALID_ADDRESS' |
  'SELF_REGISTRATION_DENIED' |
  'CARD_RENEWAL_EXPIRING_SOON' |
  'CARD_RENEWAL_SUCCESS' |
  'CARD_RENEWAL_FAILED' |
  'CARD_RENEWAL_EXPIRED' |
  'ACCOUNT_BALANCE_SUSPENSION' |
  'ACCOUNT_FROZEN_BALANCE' |
  'ACCOUNT_FROZEN_BLOCK' |
  'ACCOUNT_LOST_OR_DAMAGED_ITEMS_SUSPENSION' |
  'ACCOUNT_SUSPENSION_RELEASED' |
  'ACCOUNT_ACTIVATED' |
  'MINOR_TO_ADULT_SOON' |
  'MINOR_TO_ADULT' |
  'HOLD_AVAILABLE' |
  'HOLD_EXPIRING_SOON' |
  'HOLD_EXPIRED' |
  'HOLD_PAUSED' |
  'HOLD_RESUMED' |
  'HOLD_CANCELED' |
  'ITEM_REQUEST_SUBMITTED' |
  'ITEM_REQUEST_DENIED' |
  'ITEM_REQUEST_APPROVED' |
  'ITEM_REQUEST_FULFILLED' |
  'ITEM_REQUEST_STAFF_COMMENT' |
  'PASSWORD_RESET' |
  'PATRON_PIN_CHANGED' |
  'PAYMENT_SUBMITTED' |
  'PAYMENT_FAILED' |
  'PAYMENT_DUE' |
  'PAYMENT_METHOD_EXPIRED' |
  'CHECKOUT_AUTO_RENEWED' |
  'CHECKOUT_FINAL_OVERDUE_NOTICE' |
  'CHECKOUT_FRIENDLY_REMINDER' |
  'CHECKOUT_OCCURRED' |
  'CHECKOUT_OVERDUE' |
  'CHECKOUT_OVERDUE_WITH_FINES' |
  'CHECKOUT_RENEWED' |
  'CHECKOUT_CLAIM_PATRON_CHARGED' |
  'CHECKOUT_CLAIM' |
  'CHECKOUT_CLAIM_FEE_ASSESSED' |
  'CHECKOUT_CLAIM_MISSING_MARKED_LOST' |
  'CHECKOUT_CLAIM_MISSING_LOCATED' |
  'CHECKOUT_CLAIM_FOUND_OR_WAIVED' |
  'USER_CREATED' |
  'OPAC_WELCOME_EMAIL' |
  'DAMAGED_ITEM' |
  'BALANCE_PAYOUT' |
  'EMAIL_CHANGE_REQUEST' |
  'OPAC_REGISTRATION_VERIFICATION_EMAIL';

// Possible icon names for a notification
export type NotificationIconName = 'check_circle' | 'notifications' | 'error_outline' | 'info';

// Icon Types
export type NotificationIconType = 'success' | 'error' | 'bell' | 'info';

// Icon definition
export interface NotificationIcon {
  readonly iconName: NotificationIconName;
  readonly iconClasses: string;
}

// All information regarding a notification's type
export interface NotificationTypeDetails {
  label: string;
  icon: NotificationIconType;
}

// Definitions for each NotificationIconType
export const NOTIFICATION_ICON_CONFIG: Record<NotificationIconType, NotificationIcon> = {
  success: {
    iconName: 'check_circle',
    iconClasses: 'text-primary'
  },
  error: {
    iconName: 'error_outline',
    iconClasses: 'text-error material-icons-outlined'
  },
  bell: {
    iconName: 'notifications',
    iconClasses: 'text-primary material-icons-outlined'
  },
  info: {
    iconName: 'info',
    iconClasses: 'text-on-surface-variant material-icons-outlined'
  }
};

// Configuration of every possible notification type
export const NOTIFICATION_TYPE_CONFIG: Record<NotificationType, NotificationTypeDetails> = {
  SELF_REGISTRATION_RESIDENT_APPROVED: {
    label: 'Application Approved',
    icon: 'success'
  },
  SELF_REGISTRATION_NONRESIDENT_APPROVED: {
    label: 'Application Approved',
    icon: 'success'
  },
  SELF_REGISTRATION_OUT_OF_BOUNDS_NO_FEE: {
    label: 'Your account could not be processed',
    icon: 'error'
  },
  SELF_REGISTRATION_INVALID_ADDRESS: {
    label: 'Your account is pending library approval',
    icon: 'info'
  },
  SELF_REGISTRATION_DENIED: {
    label: 'Your account has been denied',
    icon: 'error'
  },
  CARD_RENEWAL_EXPIRING_SOON: {
    label: 'Your account is expiring soon',
    icon: 'bell'
  },
  CARD_RENEWAL_SUCCESS: {
    label: 'Your account has been renewed',
    icon: 'success'
  },
  CARD_RENEWAL_FAILED: {
    label: 'Your account has expired',
    icon: 'error'
  },
  CARD_RENEWAL_EXPIRED: {
    label: 'Your account has expired',
    icon: 'error'
  },
  ACCOUNT_BALANCE_SUSPENSION: {
    label: 'Your account has been suspended',
    icon: 'error'
  },
  ACCOUNT_FROZEN_BALANCE: {
    label: 'Your library account is frozen',
    icon: 'error'
  },
  ACCOUNT_FROZEN_BLOCK: {
    label: 'Your library account is frozen',
    icon: 'error'
  },
  ACCOUNT_LOST_OR_DAMAGED_ITEMS_SUSPENSION: {
    label: 'Your account has been suspended',
    icon: 'error'
  },
  ACCOUNT_SUSPENSION_RELEASED: {
    label: 'Your account has been restored',
    icon: 'info'
  },
  ACCOUNT_ACTIVATED: {
    label: 'Your account has been activated',
    icon: 'success'
  },
  MINOR_TO_ADULT_SOON: {
    label: 'Your account will expire on your birthday',
    icon: 'bell'
  },
  MINOR_TO_ADULT: {
    label: 'Your account has expired',
    icon: 'error'
  },
  HOLD_AVAILABLE: {
    label: 'Hold is available for pickup',
    icon: 'success'
  },
  HOLD_EXPIRING_SOON: {
    label: 'Hold is about to expire',
    icon: 'bell'
  },
  HOLD_EXPIRED: {
    label: 'Hold has expired',
    icon: 'error'
  },
  HOLD_PAUSED: {
    label: 'Hold paused',
    icon: 'bell'
  },
  HOLD_RESUMED: {
    label: 'Hold resumed',
    icon: 'bell'
  },
  HOLD_CANCELED: {
    label: 'Hold cancelled',
    icon: 'info'
  },
  ITEM_REQUEST_SUBMITTED: {
    label: 'We have received your item request',
    icon: 'info'
  },
  ITEM_REQUEST_DENIED: {
    label: 'Item request denied',
    icon: 'info'
  },
  ITEM_REQUEST_APPROVED: {
    label: 'Item request approved',
    icon: 'success'
  },
  ITEM_REQUEST_FULFILLED: {
    label: 'Item request is available for pickup',
    icon: 'success'
  },
  ITEM_REQUEST_STAFF_COMMENT: {
    label: 'A staff member has commented on your item request',
    icon: 'info'
  },
  PASSWORD_RESET: {
    label: 'Your password has been reset',
    icon: 'info'
  },
  PATRON_PIN_CHANGED: {
    label: 'Your PIN has been changed',
    icon: 'info'
  },
  PAYMENT_SUBMITTED: {
    label: 'Payment received',
    icon: 'bell'
  },
  PAYMENT_FAILED: {
    label: 'Payment has failed',
    icon: 'error'
  },
  PAYMENT_DUE: {
    label: 'Payment due',
    icon: 'info'
  },
  PAYMENT_METHOD_EXPIRED: {
    label: 'Payment on file is expired',
    icon: 'error'
  },
  CHECKOUT_AUTO_RENEWED: {
    label: 'Your item(s) have been renewed',
    icon: 'success'
  },
  CHECKOUT_FINAL_OVERDUE_NOTICE: {
    label: 'You have item(s) overdue',
    icon: 'error'
  },
  CHECKOUT_FRIENDLY_REMINDER: {
    label: 'You have item(s) due soon',
    icon: 'bell'
  },
  CHECKOUT_OCCURRED: {
    label: 'Item(s) have been checked out',
    icon: 'success'
  },
  CHECKOUT_OVERDUE: {
    label: 'Your checkout is overdue',
    icon: 'error'
  },
  CHECKOUT_OVERDUE_WITH_FINES: {
    label: 'Your checkout is overdue',
    icon: 'error'
  },
  CHECKOUT_RENEWED: {
    label: 'Checkout renewed',
    icon: 'success'
  },
  CHECKOUT_CLAIM_PATRON_CHARGED: {
    label: 'Your claim has been settled',
    icon: 'success'
  },
  CHECKOUT_CLAIM: {
    label: 'We have received your checkout claim',
    icon: 'bell'
  },
  CHECKOUT_CLAIM_FEE_ASSESSED: {
    label: 'An item you\'ve returned is damaged',
    icon: 'error'
  },
  CHECKOUT_CLAIM_MISSING_MARKED_LOST: {
    label: 'Your checkout claim item has been marked as lost',
    icon: 'error'
  },
  CHECKOUT_CLAIM_MISSING_LOCATED: {
    label: 'Item marked as missing has been located',
    icon: 'bell'
  },
  CHECKOUT_CLAIM_FOUND_OR_WAIVED: {
    label: 'Your claim was settled',
    icon: 'success'
  },
  USER_CREATED: {
    label: 'User created',
    icon: 'success'
  },
  OPAC_WELCOME_EMAIL: {
    label: 'Welcome to our library!',
    icon: 'success'
  },
  DAMAGED_ITEM: {
    label: 'Item was marked as damaged',
    icon: 'error'
  },
  BALANCE_PAYOUT: {
    label: 'Funds disbursed',
    icon: 'bell'
  },
  EMAIL_CHANGE_REQUEST: {
    label: 'Please verify your Email',
    icon: 'bell'
  },
  OPAC_REGISTRATION_VERIFICATION_EMAIL: {
    label: 'Complete your library registration',
    icon: 'bell'
  }
};

export const AMOUNT_OF_VISIBLE_NOTIFICATIONS: number = 5;
